import type { ProjectScope } from '../sync/project-scope';
import { enqueueStructuredEntityWrite, type StructuredEntityWriteRegistration } from '../sync/structured-entity-write';
import type { CastAssignment } from './types';

const CAST_ASSIGNMENT_WRITE_AUTH_ERROR = 'Authentication required to preserve casting assignment writes';

const CAST_ASSIGNMENT_WRITE_REGISTRATION: StructuredEntityWriteRegistration = {
    feature: 'people_casting',
    capability: 'EDIT_CASTING_ASSIGNMENTS',
    conflictPrefix: 'castAssignments',
    allowedKinds: ['patchEntity', 'deleteEntity'],
    authErrorMessage: CAST_ASSIGNMENT_WRITE_AUTH_ERROR,
};

export type CastAssignmentDraft = Pick<CastAssignment, 'characterId' | 'personnelId' | 'role'>
    & Partial<Pick<CastAssignment, 'id' | 'notes' | 'lastModifiedBy'>>;

export type CastAssignmentWriteOptions = {
    actorId?: string;
    idFactory?: () => string;
};

function createCastAssignmentId(idFactory?: () => string): string {
    return idFactory?.() ?? globalThis.crypto.randomUUID();
}

function findSameRelationship(
    draft: Pick<CastAssignment, 'characterId' | 'personnelId'>,
    assignments: readonly CastAssignment[]
): CastAssignment | undefined {
    return assignments.find(assignment => (
        assignment.characterId === draft.characterId
        && assignment.personnelId === draft.personnelId
    ));
}

function findAssignmentById(assignments: readonly CastAssignment[], id: string | undefined): CastAssignment | undefined {
    return id ? assignments.find(assignment => assignment.id === id) : undefined;
}

function resolveCastAssignmentWriteOperation(
    assignments: readonly CastAssignment[],
    draft: CastAssignmentDraft,
    options: Pick<CastAssignmentWriteOptions, 'idFactory'> = {}
): { assignment: CastAssignment; sourceAssignmentId?: string } {
    const sourceAssignment = findAssignmentById(assignments, draft.id);
    const existing = findSameRelationship(draft, assignments);
    const id = existing?.id ?? draft.id ?? createCastAssignmentId(options.idFactory);
    const notes = draft.notes ?? existing?.notes ?? sourceAssignment?.notes;
    const lastModifiedBy = draft.lastModifiedBy ?? existing?.lastModifiedBy ?? sourceAssignment?.lastModifiedBy;

    const assignment = {
        id,
        characterId: draft.characterId,
        personnelId: draft.personnelId,
        role: draft.role,
        ...(notes !== undefined ? { notes } : {}),
        ...(lastModifiedBy !== undefined ? { lastModifiedBy } : {}),
    };

    return {
        assignment,
        ...(sourceAssignment && sourceAssignment.id !== id ? { sourceAssignmentId: sourceAssignment.id } : {}),
    };
}

export function resolveCastAssignmentWrite(
    assignments: readonly CastAssignment[],
    draft: CastAssignmentDraft,
    options: Pick<CastAssignmentWriteOptions, 'idFactory'> = {}
): CastAssignment {
    return resolveCastAssignmentWriteOperation(assignments, draft, options).assignment;
}

export async function saveCastAssignment(
    scope: ProjectScope,
    assignments: readonly CastAssignment[],
    draft: CastAssignmentDraft,
    options: CastAssignmentWriteOptions = {}
): Promise<CastAssignment> {
    const { assignment, sourceAssignmentId } = resolveCastAssignmentWriteOperation(assignments, draft, options);

    await enqueueStructuredEntityWrite({
        scope,
        registration: CAST_ASSIGNMENT_WRITE_REGISTRATION,
        entityId: assignment.id,
        kind: 'patchEntity',
        payload: assignment,
        actorId: options.actorId,
    });

    if (sourceAssignmentId) {
        await enqueueStructuredEntityWrite({
            scope,
            registration: CAST_ASSIGNMENT_WRITE_REGISTRATION,
            entityId: sourceAssignmentId,
            kind: 'deleteEntity',
            payload: { id: sourceAssignmentId },
            actorId: options.actorId,
        });
    }

    return assignment;
}

export async function deleteCastAssignment(
    scope: ProjectScope,
    assignmentId: string,
    options: CastAssignmentWriteOptions = {}
): Promise<void> {
    await enqueueStructuredEntityWrite({
        scope,
        registration: CAST_ASSIGNMENT_WRITE_REGISTRATION,
        entityId: assignmentId,
        kind: 'deleteEntity',
        payload: { id: assignmentId },
        actorId: options.actorId,
    });
}
